"use client";

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Icon } from "@/lib/icons/Icon";
import { useT } from "@/lib/i18n/context";
import { toast } from "@/lib/notifications/toast";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import { CART_OPEN_EVENT } from "@/lib/cart/cartEvents";
import type {
  WCardContent,
  WCardProduct,
  WCardProductVariant,
} from "@/lib/api/cms-window-content";
import { wCardTranslations, type WCardTranslations } from "./WCard.i18n";
import styles from "./WCard.module.css";

/** Threshold ilosci wyswietlanej "na skroty" - powyzej pokazujemy "99+". */
const BADGE_OVERFLOW_THRESHOLD = 99;

/** Feature type z legacy IMPERIAL M_SHOP_OPTIONS - "kolor / tekstura". */
const FEATURE_TYPE_COLOR_OR_TEXTURE = 3;

/**
 * WCard - okno koszyka zakupowego (`w_card`).
 *
 * Trigger to ikona koszyka w headerze z badge'em licznika. Klik otwiera slide-in
 * drawer z lista pozycji (miniatura, nazwa, warianty, stepper ilosci, cena
 * linii, przycisk usuniecia). Stopka: laczna wartosc brutto + "Kontynuuj
 * zakupy" (zamyka drawer) + "Zamawiam" (link do checkout).
 *
 * **Reguly UX (parity z legacy Smarty `w_card_default_modern.imp` + JS
 * `w_card.js`):**
 * - `is_in_card === true` (user na podstronie /koszyk) -> klik w trigger =
 *   navigacja do `href.card`, drawer NIE otwiera sie (zabezpieczenie przed
 *   dublowaniem UI z pelnym widokiem koszyka).
 * - Badge pokazuje `total.count`, powyzej 99 -> "99+".
 * - Empty state (products=[]): drawer renderuje placeholder, stopka ukryta
 *   (nie ma czego platnic).
 * - Stepper ilosci: `min=1`, `max=stock`; input clamp na blur; przycisk minus
 *   disabled gdy count=1; przycisk plus disabled gdy count>=stock.
 * - Delete: usuniecie ostatniej pozycji zamyka drawer.
 *
 * **Mutacje (POST JSON):** `endpoints.update_count` / `endpoints.delete_product`.
 * Optimistic update lokalnego state'u; przy bledzie rollback + toast.error.
 * Router refresh po sukcesie odswieza SSR content (zeby inne okna np. header
 * z licznikiem tez zobaczyly nowy stan).
 *
 * **Escape + click-outside** zamykaja drawer. Body scroll lock podczas open
 * (zeby scroll za overlay'em nie wpadal w klik przez drawer).
 */
export function WCard({ content }: WCardProps) {
  const t = useT(wCardTranslations);
  const router = useRouter();

  /* Guard - content moze byc null gdy backend rzucil wyjatek per-okno w batch
     bootstrap'cie. Wtedy okno nie renderuje sie w headerze (visitor nie widzi
     zepsutego elementu). */
  if (!content) {
    return null;
  }

  return <CartWidget content={content} t={t} router={router} />;
}

export type WCardProps = {
  content: WCardContent | null;
};

/* ─── Widget (trigger + drawer) ─────────────────────────────────────────── */

function CartWidget({
  content,
  t,
  router,
}: {
  content: WCardContent;
  t: WCardTranslations;
  router: ReturnType<typeof useRouter>;
}) {
  /* Lokalny stan mirror'uje `content` — pozwala robic optimistic updates bez
     czekania na SSR refresh. Przy zewnetrznej zmianie `content` (np. router
     refresh po dodaniu produktu na innej stronie) sync przez useEffect. */
  const [products, setProducts] = useState<WCardProduct[]>(content.products);
  const [totalCount, setTotalCount] = useState(content.total.count);
  const [totalBrutto, setTotalBrutto] = useState(content.total.price_brutto);
  const [busyIds, setBusyIds] = useState<ReadonlySet<number>>(new Set());
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setProducts(content.products);
    setTotalCount(content.total.count);
    setTotalBrutto(content.total.price_brutto);
  }, [content.products, content.total.count, content.total.price_brutto]);

  /* Suppress drawer gdy user jest juz na podstronie koszyka (parity z legacy
     `is_in_card` - klik w ikone = navigacja do href.card zamiast overlay'a). */
  const cardHref = extractInternalHref(content.href.card);
  const orderHref = extractInternalHref(content.href.order);

  const openDrawer = useCallback(() => {
    if (content.is_in_card) return;
    setOpen(true);
  }, [content.is_in_card]);

  /* Nasluch globalnego eventu `cart:open` - dispatchowanego przez ProductCard
     (i inne miejsca dodania do koszyka) po sukcesie add-to-cart. Pozwala
     otworzyc drawer z zewnatrz drzewa headera bez propa/contextu. */
  useEffect(() => {
    const onOpen = () => openDrawer();
    window.addEventListener(CART_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(CART_OPEN_EVENT, onOpen);
  }, [openDrawer]);

  const closeDrawer = useCallback(() => {
    setOpen(false);
  }, []);

  /* ESC + body scroll lock + click outside zamykaja drawer. */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeDrawer();
    };
    const onClickOutside = (e: MouseEvent) => {
      /* Ignoruj klik na trigger'ze (rootRef) - inaczej flash open/close przy
         ponownym kliknieciu w ikone gdy drawer jest otwarty. Drawer to portal
         wizualny (position: fixed), ale drzewowy sibling `.root`. */
      if (
        drawerRef.current &&
        !drawerRef.current.contains(e.target as Node) &&
        rootRef.current &&
        !rootRef.current.contains(e.target as Node)
      ) {
        closeDrawer();
      }
    };
    document.addEventListener("keydown", onKey);
    /* mousedown zamiast click - inaczej klik na trigger'u wewnatrz overlay'a
       najpierw otworzylby drawer, a potem click-outside by go zamknal. */
    document.addEventListener("mousedown", onClickOutside);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClickOutside);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, closeDrawer]);

  const setItemBusy = useCallback((cId: number, busy: boolean) => {
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (busy) next.add(cId);
      else next.delete(cId);
      return next;
    });
  }, []);

  /* Recompute sum + count z aktualnej listy produktow (single source of truth
     - unikamy driftu miedzy licznikiem w headerze a sumy w drawerze). */
  const recomputeTotals = useCallback((items: WCardProduct[]) => {
    let count = 0;
    let brutto = 0;
    for (const p of items) {
      count += p.count;
      brutto += p.price_brutto * p.count;
    }
    setTotalCount(count);
    setTotalBrutto(brutto);
  }, []);

  const updateCount = useCallback(
    async (cId: number, nextCount: number) => {
      if (content.card_id === null) return;

      const previous = products;
      const optimistic = products.map((p) =>
        p.c_id === cId ? { ...p, count: nextCount } : p,
      );
      setProducts(optimistic);
      recomputeTotals(optimistic);
      setItemBusy(cId, true);

      try {
        const res = await fetch("/api/w-card/update-count", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            product_index: cId,
            count: nextCount,
            card_id: content.card_id,
          }),
        });
        if (!res.ok) throw new Error(t.updateFailed);
        router.refresh();
      } catch {
        setProducts(previous);
        recomputeTotals(previous);
        toast.error(t.updateFailed);
      } finally {
        setItemBusy(cId, false);
      }
    },
    [
      content.card_id,
      content.window_id,
      products,
      recomputeTotals,
      router,
      setItemBusy,
      t.updateFailed,
    ],
  );

  const deleteItem = useCallback(
    async (cId: number) => {
      if (content.card_id === null) return;

      const previous = products;
      const optimistic = previous.filter((p) => p.c_id !== cId);
      setProducts(optimistic);
      recomputeTotals(optimistic);
      setItemBusy(cId, true);

      try {
        const res = await fetch("/api/w-card/delete-product", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            product_index: cId,
            card_id: content.card_id,
          }),
        });
        if (!res.ok) throw new Error(t.deleteFailed);
        /* Legacy parity: usuniecie ostatniej pozycji -> zamknij drawer. */
        if (optimistic.length === 0) closeDrawer();
        router.refresh();
      } catch {
        setProducts(previous);
        recomputeTotals(previous);
        toast.error(t.deleteFailed);
      } finally {
        setItemBusy(cId, false);
      }
    },
    [
      content.card_id,
      content.window_id,
      products,
      recomputeTotals,
      router,
      setItemBusy,
      t.deleteFailed,
      closeDrawer,
    ],
  );

  const badgeText =
    totalCount > BADGE_OVERFLOW_THRESHOLD ? "99+" : String(totalCount);

  return (
    <div className={styles.root} ref={rootRef}>
      {content.is_in_card && cardHref !== null ? (
        <Link
          href={cardHref}
          className={styles.trigger}
          aria-label={t.triggerAriaLabel}
        >
          <TriggerBody badgeText={badgeText} totalCount={totalCount} />
        </Link>
      ) : (
        <button
          type="button"
          className={styles.trigger}
          onClick={openDrawer}
          aria-label={t.triggerAriaLabel}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <TriggerBody badgeText={badgeText} totalCount={totalCount} />
        </button>
      )}

      {open && (
        <>
          <div className={styles.overlay} aria-hidden="true" />
          <aside
            ref={drawerRef}
            className={styles.drawer}
            role="dialog"
            aria-modal="true"
            aria-label={t.drawerTitle}
          >
            <div className={styles.header}>
              <h2 className={styles.title}>{t.drawerTitle}</h2>
              <button
                type="button"
                className={styles.closeBtn}
                onClick={closeDrawer}
                aria-label={t.drawerClose}
              >
                <Icon name="x" />
              </button>
            </div>

            <div className={styles.body}>
              {products.length === 0 ? (
                <div className={styles.empty}>{t.emptyState}</div>
              ) : (
                <ul className={styles.list}>
                  {products.map((product) => (
                    <CartLine
                      key={product.c_id}
                      product={product}
                      currency={content.currency}
                      busy={busyIds.has(product.c_id)}
                      onChangeCount={updateCount}
                      onDelete={deleteItem}
                      onNavigate={closeDrawer}
                      t={t}
                    />
                  ))}
                </ul>
              )}
            </div>

            {products.length > 0 && (
              <div className={styles.footer}>
                <div className={styles.totalRow}>
                  <span className={styles.totalLabel}>{t.toPay}:</span>
                  <span className={styles.totalValue}>
                    {t.formatPrice(totalBrutto, content.currency)}
                  </span>
                </div>
                <div className={styles.actions}>
                  <button
                    type="button"
                    className={styles.continueBtn}
                    onClick={closeDrawer}
                  >
                    {t.continueShopping}
                  </button>
                  {orderHref !== null ? (
                    <Link
                      href={orderHref}
                      className={styles.payBtn}
                      onClick={closeDrawer}
                    >
                      {t.pay}
                    </Link>
                  ) : (
                    <a href={content.href.order} className={styles.payBtn}>
                      {t.pay}
                    </a>
                  )}
                </div>
              </div>
            )}
          </aside>
        </>
      )}
    </div>
  );
}

/* ─── Trigger body (icon + badge) ───────────────────────────────────────── */

function TriggerBody({
  badgeText,
  totalCount,
}: {
  badgeText: string;
  totalCount: number;
}) {
  return (
    <>
      <Icon name="shopping-cart" className={styles.triggerIcon} />
      <span
        className={
          totalCount === 0 ? `${styles.badge} ${styles.badgeZero}` : styles.badge
        }
        aria-hidden="true"
      >
        {badgeText}
      </span>
    </>
  );
}

/* ─── Single cart line ──────────────────────────────────────────────────── */

function CartLine({
  product,
  currency,
  busy,
  onChangeCount,
  onDelete,
  onNavigate,
  t,
}: {
  product: WCardProduct;
  currency: string;
  busy: boolean;
  onChangeCount: (cId: number, count: number) => void | Promise<void>;
  onDelete: (cId: number) => void | Promise<void>;
  onNavigate: () => void;
  t: WCardTranslations;
}) {
  const productHref = extractInternalHref(product.href);
  const lineBrutto = product.price_brutto * product.count;
  const lineBruttoBeforePromotion =
    product.promocja && product.price_brutto_before_promotion !== null
      ? product.price_brutto_before_promotion * product.count
      : null;
  const [inputValue, setInputValue] = useState(String(product.count));

  /* Sync input gdy count zmieni sie zewnetrznie (np. po odswiezeniu SSR). */
  useEffect(() => {
    setInputValue(String(product.count));
  }, [product.count]);

  const clampAndCommit = useCallback(
    (rawValue: string) => {
      const parsed = Number.parseInt(rawValue, 10);
      const min = 1;
      const max = product.stock > 0 ? product.stock : min;
      const clamped = Number.isFinite(parsed)
        ? Math.max(min, Math.min(max, parsed))
        : min;
      setInputValue(String(clamped));
      if (clamped !== product.count) {
        void onChangeCount(product.c_id, clamped);
      }
    },
    [onChangeCount, product.c_id, product.count, product.stock],
  );

  const decDisabled = busy || product.count <= 1;
  const incDisabled = busy || product.count >= product.stock;

  return (
    <li className={`${styles.item} ${busy ? styles.itemBusy : ""}`}>
      <div className={styles.thumb}>
        {product.picture?.file ? (
          productHref !== null ? (
            <Link href={productHref} onClick={onNavigate} tabIndex={-1}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={product.picture.file} alt="" loading="lazy" />
            </Link>
          ) : (
            <a href={product.href} tabIndex={-1}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={product.picture.file} alt="" loading="lazy" />
            </a>
          )
        ) : (
          <Icon name="image" className={styles.thumbPlaceholder} />
        )}
      </div>

      <div className={styles.details}>
        <h3 className={styles.name}>
          {productHref !== null ? (
            <Link href={productHref} onClick={onNavigate}>
              {product.name}
            </Link>
          ) : (
            <a href={product.href}>{product.name}</a>
          )}
        </h3>

        {product.variant.length > 0 && (
          <div className={styles.variants}>
            {product.variant.map((v, idx) => (
              <CartLineVariant key={idx} variant={v} t={t} />
            ))}
          </div>
        )}

        <div className={styles.bottom}>
          <div className={styles.count}>
            <button
              type="button"
              className={styles.countBtn}
              onClick={() =>
                void onChangeCount(product.c_id, product.count - 1)
              }
              disabled={decDisabled}
              aria-label={t.countDecrement}
            >
              <Icon name="minus" />
            </button>
            <input
              className={styles.countInput}
              type="number"
              inputMode="numeric"
              min={1}
              max={product.stock}
              value={inputValue}
              disabled={busy}
              onChange={(e) => setInputValue(e.target.value)}
              onBlur={(e) => clampAndCommit(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  clampAndCommit((e.target as HTMLInputElement).value);
                }
              }}
              aria-label={t.countLabel}
            />
            <button
              type="button"
              className={styles.countBtn}
              onClick={() =>
                void onChangeCount(product.c_id, product.count + 1)
              }
              disabled={incDisabled}
              aria-label={t.countIncrement}
            >
              <Icon name="plus" />
            </button>
          </div>
          <span className={styles.linePrice}>
            {lineBruttoBeforePromotion !== null && (
              <span
                className={styles.linePriceBefore}
                aria-label={t.priceBeforePromotion}
              >
                {t.formatPrice(lineBruttoBeforePromotion, currency)}
              </span>
            )}
            {t.formatPrice(lineBrutto, currency)}
          </span>
        </div>
      </div>

      <button
        type="button"
        className={styles.deleteBtn}
        onClick={() => void onDelete(product.c_id)}
        disabled={busy}
        aria-label={t.deleteItem}
      >
        <Icon name="trash" />
      </button>
    </li>
  );
}

/* ─── Variant chip (color/texture swatch OR text) ───────────────────────── */

function CartLineVariant({
  variant,
  t,
}: {
  variant: WCardProductVariant;
  t: WCardTranslations;
}) {
  const isColorOrTexture =
    variant.feature_type === FEATURE_TYPE_COLOR_OR_TEXTURE;
  const swatchStyle = useMemo<CSSProperties | undefined>(() => {
    if (!isColorOrTexture) return undefined;
    if (variant.feature_value_color) {
      return { background: `#${variant.feature_value_color}` };
    }
    return undefined;
  }, [isColorOrTexture, variant.feature_value_color]);

  return (
    <span className={styles.variantRow}>
      <span>{variant.feature_name}:</span>
      {isColorOrTexture ? (
        variant.feature_value_texture ? (
          /* eslint-disable-next-line @next/next/no-img-element */
          <img
            className={styles.variantSwatchImg}
            src={variant.feature_value_texture}
            alt={t.variantColorAlt}
          />
        ) : swatchStyle ? (
          <span
            className={styles.variantSwatch}
            style={swatchStyle}
            aria-hidden="true"
          />
        ) : null
      ) : null}
      <span className={styles.variantValueName}>
        {variant.feature_value_name}
      </span>
    </span>
  );
}
